import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3121ROLES } from "../U3121";
import U3121ProjectBody from "./form/u3121Project/U3121ProjectBody";
import { U3121ProjectFormToolBar } from "./form/u3121Project/U3121ProjectFormToolBar";
import U3121ProjectDeleteDlg from "./common/U3121ProjectDeleteDlg";
import U3121ProjectBillingProfileBody from "./form/u3121Project/u3121ProjectBillingProfileTab/U3121ProjectBillingProfileBody";
import U3121ProjectBillingSummaryBody from "./form/u3121Project/u3121ProjectBillingSummaryTab/U3121ProjectBillingSummaryBody";
import U3121ProjectProjectToSurveyBody from "./form/u3121Project/u3121ProjectProjectToSurveyTab/U3121ProjectProjectToSurveyBody";
import U3121ProjectEmployeeAssignmentBody from "./form/u3121Project/u3121ProjectEmployeeAssignmentTab/U3121ProjectEmployeeAssignmentBody";
import U3121ProjectTimesheetAccessBody from "./form/u3121Project/u3121ProjectTimesheetAccessTab/U3121ProjectTimesheetAccessBody";
import U3121ProjectScopePeriodBody from "./form/u3121Project/u3121ProjectScopePeriodTab/U3121ProjectScopePeriodBody";
import U3121ProjectTaskAssignmentBody from "./form/u3121Project/u3121ProjectTaskAssignmentTab/U3121ProjectTaskAssignmentBody";
import U3121ProjectWorkNatureAssignmentBody from "./form/u3121Project/u3121ProjectWorkNatureAssignmentTab/U3121ProjectWorkNatureAssignmentBody";
import { U3121ProjectSubProjectsBody } from "./form/u3121Project/u3121ProjectSubProjectsTab/U3121ProjectSubProjectsBody";
import U3121ProjectContactBody from "./form/u3121Project/u3121ProjectContactsTab/U3121ProjectContactBody";
import U3121ProjectDocumentsBody from "./form/u3121Project/u3121ProjectDocumentsTab/U3121ProjectDocumentsBody";
import U3121ProjectMinutesBody from "./form/u3121Project/u3121ProjectMinutesTab/U3121ProjectMinutesBody";
import { U3139ROLES } from "../../u3139Minutes/U3139";
import { useU3121Context } from "../U3121Context";
import { u3121ShowDocumentsTab } from "../tools/u3121ShowDocumentsTab";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { U3500ROLES } from "../../u3500GedDocumentSearch/U3500";
import {
  doChangeStatusProject,
  doConvertToProject,
  doCreateData,
  doDeleteData,
  doExportProjectOpeningFormPdf,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  emptyProject,
  emptyProjectToSurvey,
  luHandleAppiError,
  normalizeBillingProfileFromApi,
  normalizeProjectToSurveyFromApi,
  type U3121Project,
} from "../U3121Service";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU3121ExportProjectOpeningFormPdfHandlers } from "../U3121UseExportReportHandlers";
import { useU3121ProjectOpeningFormPdfNotification } from "../U3121UseProjectOpeningFormPdfNotification";
import {
  mergeGoogleCalendarSyncFromWs,
  useU3121GoogleCalendarSyncNotification,
} from "../U3121UseGoogleCalendarSyncNotification";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { unmaskPostalCode } from "../../u2000Core/tools/U2000MaskUtils";

const U3121ProjectForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- useReducer action type must satisfy React's AnyActionArg; U2000FormAction is incompatible
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { initLU, luStateHandlers, searchStateHandlers, gridStateHandlers, projectCurrentTab, setProjectCurrentTab } = useU3121Context();
  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  const handleEnterDocumentsExpanded = useCallback(() => {
    setProjectCurrentTab("tabDocuments");
    setDocumentsExpanded(true);
  }, [setProjectCurrentTab]);

  const handleExitDocumentsExpanded = useCallback(() => {
    setDocumentsExpanded(false);
    setProjectCurrentTab("tabDocuments");
  }, [setProjectCurrentTab]);

  useEffect(() => {
    setDocumentsExpanded(false);
  }, [id]);

  const validateForm = (project: U3121Project) => {
    const result: Record<string, string> = {};
    const editMode = formStateHandlers.getFormEditMode();
    if (!project.customer?.id?.toString().trim()) result["customer.id"] = t("errCustomerRequired", { ns: NS });
    // projetNumber généré par le backend à la création : pas de validation en mode ADD
    if (editMode !== U2000FormStateEnumEditMode.ADD) {
      if (!project.projetNumber?.trim()) result["projetNumber"] = t("errProjetNumberRequired", { ns: NS });
      if (project.projetNumber && project.projetNumber.length > 15) result["projetNumber"] = t("errProjetNumberMaxLength", { ns: NS });
    }
    if (!project.projetName?.trim()) result["projetName"] = t("errProjetNameRequired", { ns: NS });
    if (project.projetName && project.projetName.length > 255) result["projetName"] = t("errProjetNameMaxLength", { ns: NS });
    // Validation du profil de facturation uniquement si le projet n'inclut pas de sous-projets
    if (project.includeSubProjet !== true) {
      const bp = project.billingProfile;
      if (bp?.billable && !bp?.hourly && !bp?.fixedPrice) {
        result["billingProfile.hourly"] = t("errBillableRequiresHoraireOrForfaitaire", { ns: NS });
      }
      if (bp?.fixedPrice && (bp?.fixedAmount ?? 0) <= 0) {
        result["billingProfile.fixedAmount"] = t("errMontantForfaitaireMustBePositive", { ns: NS });
      }
      if (bp?.billable && (bp?.hourlyMarkup == null || Number(bp.hourlyMarkup) < 1)) {
        result["billingProfile.hourlyMarkup"] = t("errHourlyMarkupMinOne", { ns: NS });
      }
      if (bp?.billable && bp.billingFrequency !== "EndProject" && bp.billingFrequency !== "ByStep") {
        result["billingProfile.billingFrequency"] = t("errBillingFrequencyRequired", { ns: NS });
      }
      if (bp?.billable && bp?.hourly && (bp?.sellingRate == null || Number(bp.sellingRate) <= 0)) {
        result["billingProfile.sellingRate"] = t("errSellingRateRequired", { ns: NS });
      }
    }
    // Validation : au moins une tâche doit être sélectionnée
    if (!project.taskAssignments || project.taskAssignments.length === 0) {
      result["taskAssignments"] = t("errTaskAssignmentRequired", { ns: NS });
    }
    // Validation projet à arpenter : inclure les champs dans la validation lorsque « Inclure le projet à arpenter » est coché
    if (project.includeProjectToSurvey) {
      const pts = project.projectToSurvey;
      const hasPts = pts && typeof pts === "object";
      const lotArr = Array.isArray(pts?.lot) ? pts.lot : [];
      if (!hasPts || lotArr.length === 0 || !lotArr.some((s) => String(s ?? "").trim())) result["projectToSurvey.lot"] = t("errProjectToSurveyLotRequired", { ns: NS });
      if (!hasPts || !pts.owner?.trim()) result["projectToSurvey.owner"] = t("errProjectToSurveyOwnerRequired", { ns: NS });
      if (!hasPts || !pts.address?.trim()) result["projectToSurvey.address"] = t("errProjectToSurveyAddressRequired", { ns: NS });
      if (!hasPts || !pts.city?.trim()) result["projectToSurvey.city"] = t("errProjectToSurveyCityRequired", { ns: NS });
      const postalUnmasked = hasPts && pts.postalCode != null ? unmaskPostalCode(pts.postalCode) : "";
      if (postalUnmasked.length > 0 && postalUnmasked.length !== 6) {
        result["projectToSurvey.postalCode"] = t("errProjectToSurveyPostalCodeInvalid", { ns: NS });
      }
      if (!hasPts || !pts.country?.id?.toString().trim()) result["projectToSurvey.country.id"] = t("errProjectToSurveyCountryRequired", { ns: NS });
      if (!hasPts || !pts.state?.id?.toString().trim()) result["projectToSurvey.state.id"] = t("errProjectToSurveyStateRequired", { ns: NS });
      if (!hasPts || !pts.dueDate?.trim()) result["projectToSurvey.dueDate"] = t("errProjectToSurveyDueDateRequired", { ns: NS });
      if (!hasPts || !pts.dueDateType) result["projectToSurvey.dueDateType"] = t("errProjectToSurveyDueDateTypeRequired", { ns: NS });
    }
    return result;
  };

  /** Retourne l’id du premier onglet contenant des erreurs (pour y naviguer après validation). */
  const getFirstTabWithErrors = (errs: Record<string, string>, showBillingTab: boolean): string | null => {
    if (showBillingTab && Object.keys(errs).some((k) => k.startsWith("billingProfile."))) return "tabBillingProfile";
    if (Object.keys(errs).some((k) => k.startsWith("projectToSurvey."))) return "tabProjectToSurvey";
    if (errs["taskAssignments"]) return "tabTaskAssignment";
    return null;
  };

  const businessHandlers: U2000FormSBusinessHandlers<U3121Project> = {
    newRecord: () => {
      const newProject = { ...emptyProject };
      const orgHourlyMarkup = initLU.personalization?.orgHourlyMarkup;
      const n = orgHourlyMarkup != null && orgHourlyMarkup !== "" ? Number(orgHourlyMarkup) : NaN;
      const hourlyMarkupDefault = !Number.isNaN(n) ? n : (emptyProject.billingProfile.hourlyMarkup ?? 0);
      newProject.billingProfile = { ...emptyProject.billingProfile, billable: true, hourlyMarkup: hourlyMarkupDefault };
      newProject.projectToSurvey = {
        ...emptyProjectToSurvey,
        lot: [],
        country: initLU.defaultCountry?.[0] ?? null,
        state: initLU.defaultState?.[0] ?? null,
      };
      newProject.projectContacts = [];
      return newProject;
    },
    validateForm,
    saveForme: async (project: U3121Project, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(project);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(project, editMode);
        return;
      }
      formStateHandlers.setSubmitted(true);
      formStateHandlers.setErrors(errors);
      const firstTabWithErrors = getFirstTabWithErrors(errors, project.includeSubProjet === false);
      if (firstTabWithErrors) setProjectCurrentTab(firstTabWithErrors);
    },
    cancelForm: () => {
      if (luStateHandlers.getActionOriginFrom() === "/U3121") navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
    },
    onChangeRecordForm: (project) => {
      if (project?.id) handleGet.getData(project.id);
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => navigate("/U3121"),
  };

  const formStateHandlers = useU2000FormStateHandlers<U3121Project>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const changeStatusProject = async () => {
    const current = formStateHandlers.getData();
    if (!current?.id) return;
    const status = current.projectStatus ? "Close" : "Reopen";
    try {
      const response = await doChangeStatusProject(u2000AxiosAuthenticated, current.id, status);
      const data = response.data as (U3121Project & { id: string }) | { result?: U3121Project & { id: string } };
      const project = (data && typeof data === "object" && "result" in data ? data.result : data) as U3121Project & { id: string };
      if (!project) return;
      if (project.billingProfile) {
        project.billingProfile.billable = project.billingProfile.billable ?? false;
        normalizeBillingProfileFromApi(project.billingProfile);
      }
      if (project.includeProjectToSurvey === undefined) project.includeProjectToSurvey = project.projectToSurvey != null;
      if (!project.projectToSurvey && project.includeProjectToSurvey) project.projectToSurvey = { ...emptyProjectToSurvey };
      normalizeProjectToSurveyFromApi(project.projectToSurvey ?? undefined);
      (project as U3121Project).customer = (project as U3121Project).customer ?? current.customer ?? null;
      (project as U3121Project).projectManager = (project as U3121Project).projectManager ?? current.projectManager ?? null;
      gridStateHandlers.setDataById(project.id, project);
      formStateHandlers.setData(project);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    }
  };

  const convertToProject = async () => {
    const current = formStateHandlers.getData();
    if (!current?.id) return;
    const to = current.includeSubProjet === true ? "Project" : "SubProject";
    try {
      const response = await doConvertToProject(u2000AxiosAuthenticated, current.id, to);
      const data = response.data as (U3121Project & { id: string }) | { result?: U3121Project & { id: string } };
      const project = (data && typeof data === "object" && "result" in data ? data.result : data) as U3121Project & { id: string };
      if (!project) return;
      if (project.billingProfile) {
        project.billingProfile.billable = project.billingProfile.billable ?? false;
        normalizeBillingProfileFromApi(project.billingProfile);
      }
      if (project.includeProjectToSurvey === undefined) project.includeProjectToSurvey = project.projectToSurvey != null;
      if (!project.projectToSurvey && project.includeProjectToSurvey) project.projectToSurvey = { ...emptyProjectToSurvey };
      normalizeProjectToSurveyFromApi(project.projectToSurvey ?? undefined);
      (project as U3121Project).customer = (project as U3121Project).customer ?? current.customer ?? null;
      (project as U3121Project).projectManager = (project as U3121Project).projectManager ?? current.projectManager ?? null;
      gridStateHandlers.setDataById(project.id, project);
      formStateHandlers.setData(project);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    }
  };

  const handleExportProjectOpeningFormPdf = useU3121ExportProjectOpeningFormPdfHandlers({
    doExportProjectOpeningFormPdfFn: (projectId, payload) =>
      doExportProjectOpeningFormPdf(u2000AxiosAuthenticated, projectId, payload),
    onSuccess: () => {
      toast.success(t("msgReportGenerationStarted", { ns: NS }));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    },
  });

  const exportProjectOpeningFormPdf = (projectId: string, lang?: "fr" | "en") => {
    handleExportProjectOpeningFormPdf.exportProjectOpeningFormPdf(projectId, lang);
  };

  const extendedHandlers = {
    ...formStateHandlers,
    addNewProject: () => formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD),
    modifyProject: () => formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT),
    deleteProject: () => handleDelete.handleDelete(),
    changeStatusProject,
    convertToProject,
    exportProjectOpeningFormPdf,
  };

  const handleSearch = useU2000SearchHandlers<U3121Project>({
    doSearchFn: async (payload: U2000SearchPayload) => await doSearch(u2000AxiosAuthenticated, payload),
    onSuccess: (searchResult: U2000SearchResult<U3121Project>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    },
  });

  const handleGet = useU2000GetHandlers<U3121Project & { id: string }>({
    doGetFn: (idParam: string) => doGetData(u2000AxiosAuthenticated, idParam),
    onSuccess: (project: U3121Project & { id: string }) => {
      // S'assurer que billable est toujours défini (true ou false, jamais null/undefined) et normaliser le profil API
      if (project.billingProfile) {
        project.billingProfile.billable = project.billingProfile.billable ?? false;
        normalizeBillingProfileFromApi(project.billingProfile);
      }
      if (project.includeProjectToSurvey === undefined) project.includeProjectToSurvey = project.projectToSurvey != null;
      if (!project.projectToSurvey && project.includeProjectToSurvey) project.projectToSurvey = { ...emptyProjectToSurvey };
      normalizeProjectToSurveyFromApi(project.projectToSurvey ?? undefined);
      // Normaliser customer et projectManager (null si absents de la réponse)
      (project as U3121Project).customer = (project as U3121Project).customer ?? null;
      (project as U3121Project).projectManager = (project as U3121Project).projectManager ?? null;
      gridStateHandlers.setDataById(project.id, project);
      formStateHandlers.setData(project);
      const newPath = location.pathname.replace(/(?:create|[\w-]+)$/, String(project.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U3121", label: t("title", { ns: NS }) }, { label: `${project.projetName} (${project.projetNumber})` }]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    },
  });

  const handleSave = useU2000SaveHandlers<U3121Project>({
    doSaveFn: (project: U3121Project, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) return doCreateData(u2000AxiosAuthenticated, project);
      if (editMode === U2000FormStateEnumEditMode.EDIT) return doUpdateData(u2000AxiosAuthenticated, project.id, project);
      return Promise.resolve(undefined);
    },
    onSuccess: (project: U3121Project & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), project]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:create|[\w-]+)$/, String(project.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(project.id, project);
      }
      const currentData = formStateHandlers.getData();
      const merged: U3121Project & { id: string } = {
        ...project,
        customer: (project as U3121Project).customer ?? currentData?.customer ?? null,
        projectManager: (project as U3121Project).projectManager ?? currentData?.projectManager ?? null,
      };
      formStateHandlers.afterSaveData(merged);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
      if (luStateHandlers.getActionOriginFrom() === "/U3121") navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        const fieldErrors = { ...errorResult.fieldErrors };
        if (fieldErrors.customer) fieldErrors["customer.id"] = fieldErrors.customer;
        if (fieldErrors.projectManager) fieldErrors["projectManager.id"] = fieldErrors.projectManager;
        formStateHandlers.setErrors(fieldErrors);
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) formStateHandlers.setOtherErrors(errorResult.otherErrors);
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [formStateHandlers.getData()],
    doDeleteFn: async (idParam, row) => await doDeleteData(u2000AxiosAuthenticated, idParam, row),
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U3121Project).projetNumber, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((r) => r.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) handleGet.getData(action.id);
      else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page)
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page)
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      else if (action.action === U2000GridStateDeleteAction.LIST) navigate("/U3121", { replace: true });
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      });
    },
  });

  // Notification WebSocket : refetch du projet quand le job export PDF formulaire ouverture est terminé (uniquement en mode lecture)
  useU3121ProjectOpeningFormPdfNotification(id && id !== "create" ? id : undefined, (projectId) => {
    if (formStateHandlers.getFormState() === U2000FormStateEnumState.READ) {
      handleGet.getData(projectId);
    }
  });

  useU3121GoogleCalendarSyncNotification({
    projectId: id && id !== "create" ? id : undefined,
    subProjectId: null,
    onSyncUpdated: (update) => {
      const current = formStateHandlers.getData();
      if (!current?.projectToSurvey) return;
      const mergedSync = mergeGoogleCalendarSyncFromWs(
        update.googleCalendarSync,
        update.dateDivergence,
        current.projectToSurvey.googleCalendarSync,
      );
      const projectDueDate = update.projectDueDate?.trim();
      formStateHandlers.setData({
        ...current,
        projectToSurvey: {
          ...current.projectToSurvey,
          ...(projectDueDate ? { dueDate: projectDueDate } : {}),
          googleCalendarSync: mergedSync ?? undefined,
        },
      });
    },
  });

  // Toast + téléchargement auto pour exports (Excel, PDF) terminés (notification:new)
  useU2000ExportExcelNotification();

  // Initialisation au montage et quand id change (ex. F5 sur /U3121/:id ou /U3121/create : route "create" n'a pas de param id).
  useEffect(() => {
    const isCreateRoute = location.pathname.endsWith("/create");
    if (id === undefined) {
      if (isCreateRoute) formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      return;
    }
    if (!id || id === "create" || isCreateRoute) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      return;
    }
    handleGet.getData(id);
  }, [id, location.pathname]);

  // Rafraîchir le projet (et les sous-projets) au retour du formulaire sous-projet
  useEffect(() => {
    if (!id || id === "create") return;
    try {
      const refetchKey = "u3121_refetch_project";
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(refetchKey) === "1") {
        sessionStorage.removeItem(refetchKey);
        handleGet.getData(id);
      }
    } catch {
      // ignore
    }
  }, [id]);

  const projectData = formStateHandlers.getData();
  const showSubProjectsTab = projectData?.includeSubProjet !== false;
  /** Onglet Billing Profile visible seulement si le projet n'inclut pas de sous-projets. */
  const showBillingProfileTab = projectData?.includeSubProjet === false;
  /** Onglet Taux horaire (assignations) : pertinent seulement si facturation horaire. */
  const showEmployeeAssignmentTab = projectData?.billingProfile?.hourly === true;
  const isCreation = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const showBillingSummaryTab = Boolean(projectData?.id?.trim()) && !isCreation;
  const showDocumentsTab = u3121ShowDocumentsTab({
    projectId: projectData?.id,
    isCreation,
    hasU3500Read: hasRole(U3500ROLES.READ),
    enableGedNas: initLU.enableGedNas,
  });
  const showProjectMinutesTab = Boolean(projectData?.id?.trim()) && !isCreation && hasRole(U3139ROLES.READ);
  const tabPriority = [
    "tabBillingProfile",
    "tabBillingSummary",
    "tabProjectToSurvey",
    "tabEmployeeAssignment",
    "tabTimesheetAccess",
    "tabScopePeriod",
    "tabDocuments",
    "tabWorkNatureAssignment",
    "tabTaskAssignment",
    "tabProjectMinutes",
    "tabProjectContacts",
    "tabSubProjects",
    "tabAudits",
  ];
  const availableTabIdsForCreation = React.useMemo(
    () =>
      projectData == null
        ? []
        : [
            ...(showBillingProfileTab ? ["tabBillingProfile"] : []),
            ...(showBillingSummaryTab ? ["tabBillingSummary"] : []),
            "tabProjectToSurvey",
            ...(showEmployeeAssignmentTab ? ["tabEmployeeAssignment"] : []),
            "tabTimesheetAccess",
            "tabScopePeriod",
            "tabWorkNatureAssignment",
            "tabTaskAssignment",
            "tabProjectContacts",
            ...(showSubProjectsTab ? ["tabSubProjects"] : []),
            ...(hasRole(U3121ROLES.AUDITS) ? ["tabAudits"] : []),
          ],
    [projectData, showBillingProfileTab, showBillingSummaryTab, showEmployeeAssignmentTab, showSubProjectsTab, hasRole],
  );
  const firstTabForCreation = availableTabIdsForCreation.length > 0 ? (tabPriority.find((id) => availableTabIdsForCreation.includes(id)) ?? availableTabIdsForCreation[0]) : undefined;
  const hasRepositionedTabForCreationRef = useRef(false);
  useEffect(() => {
    if (!isCreation) {
      hasRepositionedTabForCreationRef.current = false;
      return;
    }
    if (!firstTabForCreation || hasRepositionedTabForCreationRef.current) return;
    hasRepositionedTabForCreationRef.current = true;
    setProjectCurrentTab(firstTabForCreation);
  }, [isCreation, firstTabForCreation, setProjectCurrentTab]);

  // En attente des données (ex. après F5) : afficher un chargement au lieu d’un écran vide
  if (projectData === null) {
    const isCreateRoute = location.pathname.endsWith("/create");
    if ((id && id !== "create") || isCreateRoute) {
      return (
        <div className="flex items-center justify-center p-8">
          <p className="text-gray-600">{t("msgLoading", { ns: NS, defaultValue: "Chargement..." })}</p>
        </div>
      );
    }
    return null;
  }

  const formTabs = [
    showBillingProfileTab && {
      id: "tabBillingProfile",
      label: t("tabBillingProfile", { ns: NS }),
      content: (
        <U3121ProjectBillingProfileBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          personalization={initLU.personalization}
          onValidateAfterBillingChange={(data) => {
            const errors = validateForm((data ?? formStateHandlers.getData()) as U3121Project);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    showBillingSummaryTab && {
      id: "tabBillingSummary",
      label: t("tabBillingSummary", { ns: NS }),
      content: (
        <U3121ProjectBillingSummaryBody
          projectId={projectData.id ?? ""}
          projectStatus={projectData.projectStatus === true}
        />
      ),
    },
    {
      id: "tabProjectToSurvey",
      label: t("tabProjectToSurvey", { ns: NS }),
      content: <U3121ProjectProjectToSurveyBody formStateHandlers={extendedHandlers as never} t={t} />,
    },
    showEmployeeAssignmentTab && {
      id: "tabEmployeeAssignment",
      label: t("tabEmployeeAssignment", { ns: NS }),
      content: <U3121ProjectEmployeeAssignmentBody formStateHandlers={extendedHandlers} />,
    },
    {
      id: "tabWorkNatureAssignment",
      label: t("tabWorkNatureAssignment", { ns: NS }),
      content: (
        <U3121ProjectWorkNatureAssignmentBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          onValidateAfterWorkNatureChange={(project) => {
            const errors = validateForm(project);
            formStateHandlers.setErrors(errors);
          }}
          onValidateAfterTaskChange={(project) => {
            const errors = validateForm(project);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    {
      id: "tabTaskAssignment",
      label: t("tabTaskAssignment", { ns: NS }),
      content: (
        <U3121ProjectTaskAssignmentBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          onValidateAfterTaskChange={(project) => {
            const errors = validateForm(project);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    showProjectMinutesTab && {
      id: "tabProjectMinutes",
      label: t("tabProjectMinutes", { ns: NS }),
      content: <U3121ProjectMinutesBody projectId={projectData.id ?? ""} />,
    },
    {
      id: "tabProjectContacts",
      label: t("tabProjectContacts", { ns: NS }),
      content: <U3121ProjectContactBody formStateHandlers={extendedHandlers} />,
    },
    showSubProjectsTab && { id: "tabSubProjects", label: t("tabSubProjects", { ns: NS }), content: <U3121ProjectSubProjectsBody formStateHandlers={extendedHandlers} /> },
    {
      id: "tabTimesheetAccess",
      label: t("tabTimesheetAccess", { ns: NS }),
      content: <U3121ProjectTimesheetAccessBody formStateHandlers={extendedHandlers} />,
    },
    {
      id: "tabScopePeriod",
      label: t("tabScopePeriod", { ns: NS }),
      content: <U3121ProjectScopePeriodBody formStateHandlers={extendedHandlers} />,
    },
    showDocumentsTab && {
      id: "tabDocuments",
      label: t("tabDocuments", { ns: NS }),
      content: (
        <U3121ProjectDocumentsBody
          projectId={projectData.id ?? ""}
          documentsExpanded={documentsExpanded}
          onEnterDocumentsExpanded={handleEnterDocumentsExpanded}
        />
      ),
    },

    hasRole(U3121ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={formStateHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3121ProjectAudit" />,
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  // Calculate which tabs have validation errors
  const errors = formStateHandlers.getErrors();
  const tabsWithErrors: string[] = [];
  if (showBillingProfileTab && Object.keys(errors).some((k) => k.startsWith("billingProfile."))) {
    tabsWithErrors.push("tabBillingProfile");
  }
  if (errors["taskAssignments"]) {
    tabsWithErrors.push("tabTaskAssignment");
  }
  if (Object.keys(errors).some((k) => k.startsWith("projectToSurvey."))) {
    tabsWithErrors.push("tabProjectToSurvey");
  }

  // Conserver l'onglet actif au retour du formulaire sous-projet (comme U2104 organisation) ; en création, repositionner sur le premier onglet (sync via useEffect au-dessus)
  const availableTabIds = formTabs.map((tab) => tab.id);
  const firstAvailableTab = tabPriority.find((tabId) => availableTabIds.includes(tabId)) ?? availableTabIds[0];
  const defaultTab = isCreation ? firstAvailableTab : projectCurrentTab && availableTabIds.includes(projectCurrentTab) ? projectCurrentTab : firstAvailableTab;

  return (
    <>
      <U3121ProjectDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        fillMainContentHeight={documentsExpanded}
        actionsBtns={<U3121ProjectFormToolBar formStateHandlers={extendedHandlers as never} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={extendedHandlers as never}
      >
        {!documentsExpanded ? <U3121ProjectBody formStateHandlers={extendedHandlers as never} t={t} /> : null}
        {documentsExpanded ? (
          <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
            <span className="text-sm font-medium text-primary-900">
              {t("lblDocumentsExpandedMode", { ns: NS })}
            </span>
            <button
              type="button"
              className="rounded-lg border border-primary-600 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
              onClick={handleExitDocumentsExpanded}
            >
              {t("btnDocumentsExitExpanded", { ns: NS })}
            </button>
          </div>
        ) : null}
        {formTabs.length > 0 && (
          <U2000Tab
            className={documentsExpanded ? "min-h-0 flex-1" : "mt-4"}
            fillRemainingHeight={documentsExpanded}
            hideTabList={documentsExpanded}
            tabs={formTabs}
            defaultTab={defaultTab}
            activeTab={projectCurrentTab ?? defaultTab}
            setActiveTab={(tab) => setProjectCurrentTab(typeof tab === "string" ? tab : String(tab))}
            tabsWithErrors={tabsWithErrors}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3121ProjectForm;
